import { MemoryBuffer } from '../../utils/memoryBuffer';
import { BaseMessage } from '../baseMessage';
import { MonitorInfo, AndroidSystemInfo, IOSSystemInfo, MainSystemInfo, UniversalWindowsSystemInfo, WindowsSystemInfo, SystemInfo } from '../../sysInfo';
import { MessageType, IWindowsSystemInfo, IIOSSystemInfo, ISystemInfo, IUniversalWindowsSystemInfo, IWindowsCeSystemInfo, IAndroidSystemInfo, MessageFlags, IMainSystemInfo } from 'app/modules/remote-control-ng/definitions';
import { OsDetectionService } from '../../services/osDetectionService';

/** PC_GETSYSINFO MessageType */
export class GetSysInfoMessage extends BaseMessage {
    private static MONITOR_INFO_SIZE = 3 * 4;

    constructor() {
        super();
        this.header.command = MessageType.PC_GETSYSINFO;
    }

    public response: ISystemInfo;

    // message nas no custom body content, no serializeBody() override

    public deserializeBody(memoryBuffer: MemoryBuffer): void {
        // Note: This network message uses Little Endian (LE) instead of the standard Big Endian (BE).
        // The reason is that, for historical reasons, the Windows Agent does not deserialize individual
        // properties of the structure. Instead, the entire structure is memory-copied in Windows system,
        // which inherently uses Little Endian. As a result, the network message remains
        // in LE format to avoid reordering byte fields during transmission and deserialization.
        let mainSystemInfo = new MainSystemInfo();
        mainSystemInfo.sysInfoSize = memoryBuffer.readUInt32LE();

        // System Info
        mainSystemInfo.osType = memoryBuffer.readUInt32LE();
        mainSystemInfo.cpuType = memoryBuffer.readUInt32LE();
        mainSystemInfo.cpuSize = memoryBuffer.readUInt16LE();
        mainSystemInfo.cpuCount = memoryBuffer.readUInt16LE();
        mainSystemInfo.cpuSpeed = memoryBuffer.readUInt32LE();
        mainSystemInfo.osVersion = memoryBuffer.readUInt32LE();
        mainSystemInfo.osBuild = memoryBuffer.readUInt32LE();
        mainSystemInfo.agentVersion = memoryBuffer.readUInt32LE();

        // Memory Info
        mainSystemInfo.totalMemory = memoryBuffer.readInt64LE();
        mainSystemInfo.freeMemory = memoryBuffer.readInt64LE();
        mainSystemInfo.totalVirtualMemory = memoryBuffer.readInt64LE();
        mainSystemInfo.freeVirtualMemory = memoryBuffer.readInt64LE();

        // Storage Info
        mainSystemInfo.totalStorage = memoryBuffer.readInt64LE();
        mainSystemInfo.freeStorage = memoryBuffer.readInt64LE();

        // Display Info
        mainSystemInfo.scrWidth = memoryBuffer.readUInt32LE();
        mainSystemInfo.scrHeight = memoryBuffer.readUInt32LE();
        mainSystemInfo.bitsPerPixel = memoryBuffer.readUInt32LE();

        // Network
        mainSystemInfo.connectionType = memoryBuffer.readUInt32LE();

        let platformSpecificSystemInfo;
        if (OsDetectionService.IsAndroid(mainSystemInfo.osType)) {
            platformSpecificSystemInfo = this.parseAndroidSystemInfo(memoryBuffer);
        } else if (OsDetectionService.IsWindows(mainSystemInfo.osType)) {
            platformSpecificSystemInfo = this.parseWindowsSystemInfo(memoryBuffer);
        } else if (OsDetectionService.IsWindowsCe(mainSystemInfo.osType)) {
            platformSpecificSystemInfo = this.parseWindowsCeSystemInfo(memoryBuffer);
        } else if (OsDetectionService.IsUniversalWindows(mainSystemInfo.osType)) {
            platformSpecificSystemInfo = this.parseUniversalWindowsSystemInfo(memoryBuffer);
        } else if (OsDetectionService.IsIOS(mainSystemInfo.osType)) {
            platformSpecificSystemInfo = this.parseIOSSystemInfo(memoryBuffer);
        } else if (OsDetectionService.IsLinux(mainSystemInfo.osType)) {
            // Do nothing for now.
        }
        else if (OsDetectionService.IsTizen(mainSystemInfo.osType)) {
            // Do nothing.
        }
        else {
            throw "System info contains unknown OS type";
        }
        this.response = new SystemInfo(mainSystemInfo, platformSpecificSystemInfo);
    }

    private parseAndroidSystemInfo = (memoryBuffer: MemoryBuffer): IAndroidSystemInfo => {
        var si = new AndroidSystemInfo();
        si.batteryStatus = memoryBuffer.readInt32LE();
        si.batteryLevel = memoryBuffer.readInt32LE();
        si.batteryVoltage = memoryBuffer.readInt32LE();
        si.batteryTemperature = memoryBuffer.readInt32LE();

        // Multiscreen - Monitor Info
        // uses buffer size to determine whether it contains monitor info
        if (memoryBuffer.getByteArray().length > memoryBuffer.getPosition()) {
            si.monitorNumber = memoryBuffer.readUInt32LE();
            //   console.log("Monitor Number: " + si.monitors);
            for (let i = 0; i < si.monitorNumber; i++) {
                var info = new MonitorInfo();
                info.id = memoryBuffer.readUInt32LE();
                info.width = memoryBuffer.readUInt32LE();
                info.height = memoryBuffer.readUInt32LE();
                si.monitorList.push(info);
            }
        }
        return si;
    }

    private parseWindowsSystemInfo = (memoryBuffer: MemoryBuffer): IWindowsSystemInfo => {
        let si: IWindowsSystemInfo = new WindowsSystemInfo();
        si.memoryLoad = memoryBuffer.readUInt32LE();
        si.pageSize = memoryBuffer.readUInt32LE();
        si.minAppAddress = memoryBuffer.readInt64LE();
        si.maxAppAddress = memoryBuffer.readInt64LE();
        si.monitors = memoryBuffer.readUInt32LE();
        si.versionInfo = memoryBuffer.readCharArray(80 * 2); // WCHAR[80]
        si.processorName = memoryBuffer.readCharArray(80 * 2); // WCHAR[80]

        si.batteryLifetime = memoryBuffer.readUInt32LE();
        si.batteryFullLifeTime = memoryBuffer.readUInt32LE();
        si.batteryFlag = memoryBuffer.readUInt8();
        si.batteryLifePercent = memoryBuffer.readUInt8();
        si.batteryReserved = [memoryBuffer.readUInt8(), memoryBuffer.readUInt8()]; // BYTE[2]

        si.acLineStatus = memoryBuffer.readUInt8();
        si.reserved = [
            memoryBuffer.readUInt8(),
            memoryBuffer.readUInt8(),
            memoryBuffer.readUInt8()
        ]; // BYTE[3]

        // Multiscreen - Monitor Info
        // uses buffer size to determine whether it contains monitor info, it should greater than at least one monitor info size
        if (si.monitors > 1 && memoryBuffer.getByteArray().length - memoryBuffer.getPosition() > GetSysInfoMessage.MONITOR_INFO_SIZE ) {
            for (let i = 0; i < si.monitors; i++) {
                var info = new MonitorInfo();
                info.id = memoryBuffer.readUInt32BE();
                info.width = memoryBuffer.readUInt32BE();
                info.height = memoryBuffer.readUInt32BE();
                info.top = memoryBuffer.readUInt32BE();
                info.left = memoryBuffer.readUInt32BE();
                si.monitorsInfo.push(info);
            }
        }

        return si;
    }

    private parseWindowsCeSystemInfo = (memoryBuffer: MemoryBuffer): IWindowsCeSystemInfo => {
        const si: IWindowsCeSystemInfo = this.parseWindowsSystemInfo(memoryBuffer) as IWindowsCeSystemInfo;

        si.batteryLifetime = memoryBuffer.readUInt32LE();
        si.batteryFullLifeTime = memoryBuffer.readUInt32LE();
        si.batteryFlag = memoryBuffer.readUInt8();
        si.batteryLifePercent = memoryBuffer.readUInt8();
        si.batteryReserved = [
            memoryBuffer.readUInt8(),
            memoryBuffer.readUInt8()
        ]; // BYTE[2]

        return si;
    }

    private parseUniversalWindowsSystemInfo = (memoryBuffer: MemoryBuffer): IUniversalWindowsSystemInfo => {
        var si = new UniversalWindowsSystemInfo();
        si.batteryLifePercent = memoryBuffer.readUInt8();
        return si;
    }

    private parseIOSSystemInfo = (memoryBuffer: MemoryBuffer): IIOSSystemInfo => {
        var si = new IOSSystemInfo();
        si.batteryStatus = memoryBuffer.readUInt32LE();
        si.batteryLevel = memoryBuffer.readUInt32LE();
        si.deviceModel = memoryBuffer.readCharArray(20 * 2); // WCHAR[20]
        si.deviceName = memoryBuffer.readCharArray(40 * 2); // WCHAR[40]
        return si;
    }
}
